/**
 * SEAL Configuration Page
 * Phase 2, Step 2.4 — Manage SEAL configuration hierarchy
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import SealStatusBadge from '../../components/seal/SealStatusBadge';
import adminApi from '../../services/adminApi';
import './SealConfig.css';

const VERTICALS = [
  { key: 'wms', label: 'WMS', icon: '📦' },
  { key: 'iseries', label: 'iSeries', icon: '🖥️' },
  { key: 'civil', label: 'Civil', icon: '🏗️' },
  { key: 'apparel', label: 'Apparel', icon: '👕' },
  { key: 'skillcode', label: 'SkillCode', icon: '🖥️' }
];

const STRATEGIES = ['Direct Q&A', 'Concept Explanation', 'Scenario', 'Comparison', 'Troubleshooting'];

const SealConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Global config
  const [sealEnabled, setSealEnabled] = useState(false);
  const [observationMode, setObservationMode] = useState('passive');
  const [learningRate, setLearningRate] = useState('moderate');
  const [retentionDays, setRetentionDays] = useState(90);
  const [healthData, setHealthData] = useState(null);

  // Per-vertical config
  const [verticalConfigs, setVerticalConfigs] = useState({});

  // Cascade viewer
  const [cascadeKey, setCascadeKey] = useState('seal_enabled');
  const [cascadeData, setCascadeData] = useState(null);

  // Kill switch
  const [showKillConfirm, setShowKillConfirm] = useState(false);

  // All config entries
  const [allConfigs, setAllConfigs] = useState([]);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [healthRes, configRes] = await Promise.all([
        adminApi.get('/api/seal/config/health').catch(() => ({ data: { success: false } })),
        adminApi.get('/api/seal/config').catch(() => ({ data: { success: false } }))
      ]);

      if (healthRes.data?.success) {
        const h = healthRes.data.data;
        setHealthData(h);
        setSealEnabled(!!h.seal_enabled);
        setObservationMode(h.observation_mode || 'passive');

        // Set vertical configs from health
        const vc = {};
        for (const v of VERTICALS) {
          const vd = h.verticals?.[v.key] || {};
          vc[v.key] = {
            enabled: !!vd.enabled,
            mode: vd.mode || 'off',
            threshold: 9.0,
            strategies: ['Direct Q&A', 'Concept Explanation', 'Scenario', 'Comparison', 'Troubleshooting']
          };
        }
        setVerticalConfigs(vc);
      }

      if (configRes.data?.success) {
        setAllConfigs(configRes.data.data || []);
      }
    } catch (err) {
      setError('Failed to load SEAL configuration');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = useCallback(async (key, value, level = 'global', opts = {}) => {
    try {
      await adminApi.put(`/api/seal/config/${encodeURIComponent(key)}`, {
        value, level, ...opts
      });
      setSuccess(`Updated ${key}`);
    } catch (err) {
      setError(`Failed to update ${key}`);
    }
  }, []);

  const handleToggleSeal = async (enabled) => {
    setSealEnabled(enabled);
    await updateConfig('seal_enabled', enabled);
  };

  const handleModeChange = async (mode) => {
    setObservationMode(mode);
    await updateConfig('observation_mode', mode);
  };

  const handleLearningRateChange = async (rate) => {
    setLearningRate(rate);
    await updateConfig('learning_rate', rate);
  };

  const handleRetentionChange = async (days) => {
    setRetentionDays(days);
    await updateConfig('observation_retention_days', days);
  };

  const handleVerticalToggle = async (vertical, enabled) => {
    setVerticalConfigs(prev => ({ ...prev, [vertical]: { ...prev[vertical], enabled } }));
    await updateConfig(`seal_${vertical}_enabled`, enabled);
  };

  const handleVerticalMode = async (vertical, mode) => {
    setVerticalConfigs(prev => ({ ...prev, [vertical]: { ...prev[vertical], mode } }));
    await updateConfig(`seal_${vertical}_mode`, mode, 'vertical', { vertical });
  };

  const handleKillSwitch = async () => {
    setShowKillConfirm(false);
    try {
      await adminApi.post('/api/seal/config/seed');
      setSealEnabled(false);
      await updateConfig('seal_enabled', false);
      setSuccess('Emergency rollback activated — SEAL disabled');
      loadAllData();
    } catch (err) {
      setError('Emergency rollback failed');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      await adminApi.post('/api/seal/config/seed');
      setSuccess('SEAL defaults seeded successfully');
      loadAllData();
    } catch (err) {
      setError('Failed to seed defaults');
    }
  };

  const fetchCascade = async (key) => {
    setCascadeKey(key);
    try {
      const res = await adminApi.get(`/api/seal/config/resolve/${encodeURIComponent(key)}`);
      if (res.data?.success) {
        setCascadeData(res.data.data);
      }
    } catch (err) {
      setCascadeData(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout showBackButton backPath="/admin/dashboard">
        <div className="seal-config">
          <div className="seal-config-loading">
            <div className="spinner"></div>
            <p>Loading SEAL configuration...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div className="seal-config">
        {/* Header */}
        <div className="seal-config-header">
          <div>
            <h1><span className="header-icon">⚙️</span> SEAL Configuration</h1>
            <p className="header-subtitle">Manage SEAL system settings and per-domain configuration</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <SealStatusBadge />
            <button onClick={() => navigate('/admin/seal/dashboard')} className="btn btn-secondary">
              Dashboard
            </button>
            <button onClick={handleSeedDefaults} className="btn btn-secondary">
              Seed Defaults
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="error-banner">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        {success && (
          <div className="success-banner">
            <span>✓ {success}</span>
          </div>
        )}

        {/* Section 1: Global Settings */}
        <div className="config-section">
          <h2 className="section-title">Global Settings</h2>
          <div className="config-grid">
            {/* SEAL Active Toggle */}
            <div className="config-field">
              <label>SEAL Active</label>
              <div className="toggle-switch" onClick={() => handleToggleSeal(!sealEnabled)}>
                <div className={`toggle-track ${sealEnabled ? 'on' : 'off'}`}>
                  <div className="toggle-thumb" />
                </div>
                <span className="toggle-label">{sealEnabled ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>

            {/* Observation Mode */}
            <div className="config-field">
              <label>Observation Mode</label>
              <div className="radio-group">
                {['passive', 'active', 'generating'].map(mode => (
                  <label key={mode} className={`radio-option ${observationMode === mode ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="observationMode"
                      value={mode}
                      checked={observationMode === mode}
                      onChange={() => handleModeChange(mode)}
                    />
                    <span className="radio-label">{mode.charAt(0).toUpperCase() + mode.slice(1)}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Learning Rate */}
            <div className="config-field">
              <label>Learning Rate</label>
              <select
                value={learningRate}
                onChange={(e) => handleLearningRateChange(e.target.value)}
                className="config-select"
              >
                <option value="conservative">Conservative</option>
                <option value="moderate">Moderate</option>
                <option value="aggressive">Aggressive</option>
              </select>
            </div>

            {/* Retention */}
            <div className="config-field">
              <label>Observation Retention (days)</label>
              <input
                type="number"
                value={retentionDays}
                onChange={(e) => setRetentionDays(parseInt(e.target.value) || 90)}
                onBlur={() => handleRetentionChange(retentionDays)}
                min={7}
                max={365}
                className="config-input"
              />
            </div>

            {/* Emergency Kill Switch */}
            <div className="config-field">
              <label>Emergency Kill Switch</label>
              <button
                className="btn-danger"
                onClick={() => setShowKillConfirm(true)}
              >
                Activate Emergency Rollback
              </button>
            </div>
          </div>
        </div>

        {/* Section 2: Per-Domain SEAL Flags */}
        <div className="config-section">
          <h2 className="section-title">Per-Domain Configuration</h2>
          <div className="vertical-config-grid">
            {VERTICALS.map(v => {
              const vc = verticalConfigs[v.key] || {};
              return (
                <div key={v.key} className="vertical-config-card">
                  <div className="vc-header">
                    <span className="vc-icon">{v.icon}</span>
                    <span className="vc-name">{v.label}</span>
                    <div className="toggle-switch small" onClick={() => handleVerticalToggle(v.key, !vc.enabled)}>
                      <div className={`toggle-track ${vc.enabled ? 'on' : 'off'}`}>
                        <div className="toggle-thumb" />
                      </div>
                    </div>
                  </div>

                  <div className="vc-body">
                    <div className="vc-field">
                      <label>Mode</label>
                      <select
                        value={vc.mode || 'off'}
                        onChange={(e) => handleVerticalMode(v.key, e.target.value)}
                        className="config-select"
                        disabled={!vc.enabled}
                      >
                        <option value="off">Off</option>
                        <option value="passive">Passive</option>
                        <option value="active">Active</option>
                        <option value="generating">Generating</option>
                      </select>
                    </div>

                    <div className="vc-field">
                      <label>Auto-approve Threshold</label>
                      <input
                        type="number"
                        value={vc.threshold || 9.0}
                        step="0.1"
                        min="0"
                        max="10"
                        className="config-input"
                        disabled={!vc.enabled}
                        onChange={(e) => {
                          setVerticalConfigs(prev => ({
                            ...prev,
                            [v.key]: { ...prev[v.key], threshold: parseFloat(e.target.value) }
                          }));
                        }}
                      />
                    </div>

                    <div className="vc-field">
                      <label>Active Strategies</label>
                      <div className="strategy-checkboxes">
                        {STRATEGIES.map(s => (
                          <label key={s} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={(vc.strategies || []).includes(s)}
                              disabled={!vc.enabled}
                              onChange={(e) => {
                                setVerticalConfigs(prev => {
                                  const curr = prev[v.key]?.strategies || [];
                                  const updated = e.target.checked
                                    ? [...curr, s]
                                    : curr.filter(x => x !== s);
                                  return { ...prev, [v.key]: { ...prev[v.key], strategies: updated } };
                                });
                              }}
                            />
                            <span>{s}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 3: Config Hierarchy Viewer */}
        <div className="config-section">
          <h2 className="section-title">Config Hierarchy Viewer</h2>
          <div className="cascade-viewer">
            <div className="cascade-controls">
              <select
                value={cascadeKey}
                onChange={(e) => fetchCascade(e.target.value)}
                className="config-select"
              >
                <option value="seal_enabled">seal_enabled</option>
                <option value="observation_mode">observation_mode</option>
                <option value="learning_rate">learning_rate</option>
                <option value="seal_wms_enabled">seal_wms_enabled</option>
                <option value="seal_iseries_enabled">seal_iseries_enabled</option>
                <option value="seal_civil_enabled">seal_civil_enabled</option>
                <option value="seal_apparel_enabled">seal_apparel_enabled</option>
                <option value="seal_erpnext_enabled">seal_erpnext_enabled</option>
                <option value="seal_wms_mode">seal_wms_mode</option>
                <option value="seal_iseries_mode">seal_iseries_mode</option>
                {allConfigs.map(c => (
                  <option key={c.config_key} value={c.config_key}>{c.config_key}</option>
                ))}
              </select>
              <button onClick={() => fetchCascade(cascadeKey)} className="btn btn-secondary">Resolve</button>
            </div>

            {cascadeData && (
              <div className="cascade-levels">
                {['global', 'vertical', 'tenant', 'document'].map(level => {
                  const entry = cascadeData.cascade?.find(c => c.level === level);
                  const isWinner = cascadeData.resolved_from_level === level;
                  return (
                    <div key={level} className={`cascade-level ${isWinner ? 'winner' : ''} ${entry ? 'has-value' : 'inherited'}`}>
                      <div className="cascade-level-name">
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                        {isWinner && <span className="winner-badge">ACTIVE</span>}
                      </div>
                      <div className="cascade-level-value">
                        {entry ? JSON.stringify(entry.value) : <span className="inherited-text">inherited</span>}
                      </div>
                    </div>
                  );
                })}
                <div className="cascade-resolved">
                  Resolved: <strong>{JSON.stringify(cascadeData.resolved_value)}</strong>
                  <span className="resolved-from">(from {cascadeData.resolved_from_level})</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Kill Switch Confirmation Modal */}
        {showKillConfirm && (
          <div className="modal-overlay" onClick={() => setShowKillConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
              <div className="modal-header" style={{ borderBottom: '2px solid #fecaca' }}>
                <h2 style={{ color: '#dc2626' }}>⚠️ Emergency Rollback</h2>
                <button className="modal-close" onClick={() => setShowKillConfirm(false)}>×</button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                  This will <strong>immediately disable SEAL</strong> and roll back all SEAL-managed changes.
                  All prompts will revert to their manually-configured state.
                </p>
                <p style={{ fontSize: '13px', color: '#dc2626', marginTop: '12px' }}>
                  This action cannot be easily undone.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowKillConfirm(false)}>Cancel</button>
                <button className="btn-danger" onClick={handleKillSwitch}>
                  Confirm Emergency Rollback
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SealConfig;
